{
  "app": {
    "name": "Compendio",
    "tagline": "A Markdown folder that is the database of record",
    "loading": "Loading…",
    "retry": "Try again",
    "cancel": "Cancel",
    "save": "Save",
    "saving": "Saving…",
    "delete": "Delete",
    "edit": "Edit",
    "close": "Close",
    "back": "Back",
    "next": "Next",
    "finish": "Finish",
    "confirm": "Confirm",
    "search": "Search",
    "skipToContent": "Skip to content",
    "menu": "Menu",
    "theme": {
      "label": "Theme",
      "light": "Light",
      "dark": "Dark",
      "auto": "Follow the system"
    },
    "language": {
      "label": "Language"
    },
    "error": {
      "title": "Something went wrong",
      "generic": "The request could not be completed.",
      "offline": "You appear to be offline.",
      "notFound": "That page does not exist, or you do not have access to it."
    }
  },
  "auth": {
    "signIn": "Sign in",
    "signOut": "Sign out",
    "userName": "User name",
    "password": "Password",
    "rememberMe": "Stay signed in",
    "failed": "That user name and password do not match.",
    "profile": "Profile",
    "displayName": "Display name",
    "email": "Email",
    "changePassword": "Change password",
    "currentPassword": "Current password",
    "newPassword": "New password",
    "passwordChanged": "Your password has been changed.",
    "profileSaved": "Your profile has been saved."
  },
  "setup": {
    "title": "Set up Compendio",
    "subtitle": "Four short steps. Nothing here needs a database server or an internet connection.",
    "steps": {
      "language": "Language",
      "account": "Administrator",
      "content": "Content folder",
      "access": "Default access",
      "https": "HTTPS"
    },
    "language": {
      "heading": "Which language should this instance use?",
      "help": "Everyone can choose their own afterwards. This is the default for people who have not."
    },
    "account": {
      "heading": "Create the administrator account",
      "help": "There is no email in Compendio, so keep these credentials somewhere safe. Recovery is a command on the server.",
      "passwordHint": "At least 12 characters.",
      "instanceName": "What should this wiki be called?"
    },
    "content": {
      "heading": "Where your pages live",
      "help": "Every page is a Markdown file in this folder. Open it in VS Code, back it up, sync it — it is yours.",
      "path": "Content folder"
    },
    "access": {
      "heading": "Who can read a new folder by default?",
      "everyone": "Everyone who signs in can read",
      "everyoneHelp": "The usual choice. You can restrict individual folders later.",
      "nobody": "Nobody until you say so",
      "nobodyHelp": "For an instance where most content is sensitive."
    },
    "https": {
      "heading": "HTTPS (optional)",
      "help": "Compendio can issue its own certificate — no certificate authority, no internet, no purchase. Browsers will warn until you trust it, and the install guide shows how.",
      "skip": "Skip for now",
      "issue": "Issue a certificate"
    },
    "done": "Compendio is ready."
  },
  "nav": {
    "home": "Home",
    "tree": "Pages",
    "search": "Search",
    "tags": "Tags",
    "recent": "Recently updated",
    "admin": "Administration",
    "newPage": "New page",
    "newFolder": "New folder",
    "deleteFolder": "Delete this folder",
    "deleteFolderConfirm": "Delete \"{{name}}\" and everything inside it? The pages are removed, but their history is kept and can be restored.",
    "deleteFolderCount_one": "This removes {{count}} page that you can see.",
    "deleteFolderCount_other": "This removes {{count}} pages that you can see.",
    "folderDeleted": "Folder deleted",
    "emptyTree": "No pages yet. Create the first one.",
    "collapse": "Collapse",
    "expand": "Expand",
    "dashboard": "Dashboard",
    "notifications": "Notifications",
    "stale": "Review due",
    "ask": "Ask the wiki"
  },
  "page": {
    "edit": "Edit",
    "view": "View",
    "history": "History",
    "delete": "Delete this page",
    "deleteConfirm": "Delete \"{{title}}\"? The file is removed, but its history is kept and can be restored.",
    "move": "Move or rename",
    "moveName": "Name",
    "moveDestination": "Destination folder",
    "moveRoot": "Top level",
    "moveCurrent": "current",
    "moveNoWrite": "read-only",
    "moveSubmit": "Move",
    "moved": "Moved to {{path}}",
    "moveExists": "There is already a page or folder with that name there.",
    "moveForbidden": "You do not have permission to write there.",
    "moveNameSeparator": "A name cannot contain \"/\" or \"\\\". Choose the folder below instead.",
    "moveNameIllegal": "That name cannot be used for a file. Avoid < > : \" | ? * and a trailing dot or space.",
    "deleteForbidden": "You do not have permission to delete this.",
    "updated": "Updated {{when}}",
    "updatedBy": "Updated {{when}} by {{who}}",
    "updatedExternally": "Updated {{when}} in the content folder",
    "owner": "Owner",
    "tags": "Tags",
    "tableOfContents": "On this page",
    "backlinks": "Linked from",
    "noBacklinks": "No other page links here yet.",
    "translations": "Other languages",
    "translationStale": "The source page has changed since this translation was written.",
    "onlyAvailableIn": "This page is only available in {{language}}.",
    "attachments": "Attachments",
    "addAttachment": "Add a file",
    "attachmentAdded": "File attached.",
    "attachmentFailed": "The file could not be attached.",
    "deleteAttachment": "Delete the file",
    "deleteAttachmentNamed": "Delete \"{{name}}\"",
    "deleteAttachmentConfirm": "Delete \"{{name}}\"? It is removed from this page and the file itself is deleted. The edit to the page is kept in its history, but the file cannot be restored from there.",
    "attachmentDeleted": "File deleted.",
    "attachmentDeleteFailed": "The file could not be deleted.",
    "attachmentDeleteConflict": "The page was being saved at that moment, so nothing has been deleted. Try again.",
    "image": "Image",
    "imageZoom": "Show this image at full size",
    "imageZoomNamed": "Show \"{{name}}\" at full size",
    "imageDimensions": "{{width}} × {{height}} px",
    "imageOriginal": "Open the original",
    "secure": "Encrypted folder",
    "secureHint": "Anyone with read access can read this page. Only administrators can change it.",
    "notCanonical": "This page was written outside Compendio. The first save here will tidy its formatting once.",
    "empty": "This page is empty. Press Edit to start writing.",
    "created": "Page created.",
    "saved": "Saved.",
    "deleted": "Page deleted.",
    "changeTitle": "Change title",
    "titleChanged": "The title has been changed.",
    "titleLabel": "Title",
    "newTitle": "What is this page about?",
    "template": "Template",
    "templateHint": "A starting shape. Delete anything you do not need."
  },
  "editor": {
    "placeholder": "Write here. Press / for a menu.",
    "markdownSource": "Markdown source",
    "richText": "Formatted",
    "unsavedChanges": "You have unsaved changes.",
    "leaveConfirm": "Leave without saving? Your changes will be lost.",
    "draftRecovered": "Recovered an unsaved draft from {{when}}.",
    "discardDraft": "Discard the draft",
    "saveFailed": "Could not save. Your text is still here — try again.",
    "saveForbidden": "You do not have permission to save here. Your text is still here — ask an administrator for write access to this folder.",
    "image": {
      "uploading": "Attaching the image…",
      "uploadButton": "Choose an image",
      "orPasteLink": "or paste a link",
      "caption": "Write a caption",
      "confirm": "Confirm",
      "needsSave": "Save the page first, then images can be attached to it.",
      "failed": "The image could not be attached.",
      "onlyImages": "Only images can be dropped into the text. Use \"Add a file\" on the page for anything else."
    },
    "conflict": {
      "title": "This page changed while you were editing",
      "explain": "Somebody else — or an edit made in the content folder — saved after you opened this page. Nothing is lost. Choose what to keep.",
      "mine": "My version",
      "theirs": "Their version",
      "takeMine": "Keep mine",
      "takeTheirs": "Keep theirs",
      "resolveOnDesktop": "Merging needs a wider screen. Open this page on a computer to resolve it.",
      "resolved": "Merged and saved."
    },
    "menu": {
      "text": "Text",
      "list": "List",
      "advanced": "Advanced"
    },
    "toolbar": {
      "normalText": "Normal text",
      "headingLevel": "Heading {{level}}",
      "bold": "Bold",
      "italic": "Italic",
      "heading": "Heading",
      "bulletList": "Bulleted list",
      "orderedList": "Numbered list",
      "taskList": "Checklist",
      "quote": "Quote",
      "code": "Code",
      "codeBlock": "Code block",
      "link": "Link",
      "image": "Image",
      "table": "Table",
      "divider": "Divider",
      "diagram": "Diagram"
    },
    "paste": {
      "converted": "Pasted content was converted to formatted text.",
      "plainText": "Paste without formatting"
    }
  },
  "search": {
    "placeholder": "Search titles and content…",
    "results_one": "{{count}} result",
    "results_other": "{{count}} results",
    "noResults": "Nothing matched \"{{query}}\".",
    "noResultsHint": "Try fewer words, or check the spelling.",
    "quickSwitcher": "Jump to a page",
    "filters": "You can use tag:, in:, owner:, lang: and updated:> in the search box.",
    "inLanguage": "in {{language}}",
    "indexRebuilding": "The search index is rebuilding. Results may be incomplete for a moment."
  },
  "help": {
    "title": "Guide",
    "forAdministrators": "For administrators",
    "untranslated": "This topic has not been translated yet, so it is shown in English.",
    "notFound": "That help topic does not exist.",
    "empty": "The guide is not available on this instance."
  },
  "history": {
    "title": "History of {{title}}",
    "versions": "Versions",
    "compare": "Compare",
    "restore": "Restore this version",
    "restoreConfirm": "Restore the version from {{when}}? This adds a new version rather than removing anything, so it can be undone.",
    "restored": "Restored. A new version records the change.",
    "sourceDiff": "Source",
    "renderedDiff": "Rendered",
    "added": "added",
    "removed": "removed",
    "source": {
      "Editor": "Edited here",
      "External": "Edited in the content folder",
      "Move": "Moved",
      "Delete": "Deleted",
      "Restore": "Restored",
      "Normalization": "Formatting tidied"
    },
    "noAuthor": "No author — this change came from the content folder"
  },
  "admin": {
    "title": "Administration",
    "users": "People",
    "groups": "Groups",
    "access": "Access",
    "secure": "Encrypted folders",
    "settings": "Settings",
    "user": {
      "add": "Add a person",
      "created": "The person has been added.",
      "role": "Role",
      "roles": {
        "Reader": "Reader",
        "Editor": "Editor",
        "Admin": "Administrator"
      },
      "roleHelp": "A role is a ceiling, not a grant. A Reader can never write, whatever a folder's rules say.",
      "active": "Active",
      "saved": "The person's details have been saved.",
      "leaveBlankPassword": "Leave blank to keep the current password.",
      "lastSignIn": "Last signed in",
      "never": "Never",
      "resetPassword": "Set a new password",
      "lastAdmin": "There must always be one active administrator."
    },
    "group": {
      "add": "Add a group",
      "name": "Name",
      "created": "The group has been added.",
      "rename": "Rename",
      "renamed": "The group has been renamed.",
      "members": "Members",
      "manageMembers": "Manage members",
      "membersPlaceholder": "Choose people to add",
      "membersSaved": "The group's members have been saved.",
      "noPeople": "No matching people",
      "memberCount_one": "{{count}} member",
      "memberCount_other": "{{count}} members"
    },
    "acl": {
      "title": "Access to {{path}}",
      "inherit": "Inherit from {{parent}}",
      "inheritRoot": "Inherit the instance default",
      "restricted": "Restricted — only the people and groups below",
      "currently": "Currently: {{summary}}",
      "addSubject": "Add a person or group",
      "everyone": "Everyone who signs in",
      "levels": {
        "None": "No access",
        "Read": "Read",
        "Write": "Write",
        "Manage": "Manage"
      },
      "preview": "Effective access preview",
      "previewFor": "What can this person do here?",
      "previewResult": "{{name}} can {{level}}",
      "reason": {
        "role.admin": "because they are an administrator",
        "role.ceiling.Reader": "capped by their Reader role",
        "role.ceiling.Editor": "capped by their Editor role",
        "secure.readOnly": "because this folder is encrypted, and only administrators can change it",
        "instance.default": "from the instance default",
        "inherited": "inherited from a parent folder"
      },
      "noDeny": "There are no deny rules. To take access away, cut inheritance and list who gets in.",
      "saved": "Access rules saved."
    },
    "secureScope": {
      "add": "Encrypt a folder",
      "path": "Folder",
      "threatModel": "This protects a stolen disk, a backup archive, a mis-synced folder and a future git mirror. It does not protect against an administrator of this server, and it does not hide folder or file names.",
      "filesFirstSuspended": "Files-first is suspended inside an encrypted folder: runbook.md.enc does not open in VS Code. Use compendio secure export and import for a direct edit.",
      "indexContent": "Include these pages in search",
      "indexContentWarning": "Page contents will be stored unencrypted in the search index inside compendio.db. Anyone with the database file will be able to read them.",
      "allowAi": "Allow AI features to read these pages",
      "allowAiConfirm": "The contents of this encrypted folder will be sent to {{endpoint}} whenever somebody uses an AI action on a page inside it. Continue?",
      "allowAiOn": "Pages here are sent to {{endpoint}} when an AI action is used on them.",
      "allowAiOff": "The AI assistant cannot read these pages, and will not cite them.",
      "keyStatus": "Key",
      "available": "Readable",
      "unavailable": "Unavailable — run compendio doctor on the server",
      "created": "The folder is now encrypted."
    },
    "status": {
      "title": "Status",
      "version": "Version",
      "installMode": "Installed as",
      "contentRoot": "Content folder",
      "pages": "Pages",
      "folders": "Folders",
      "watcher": "File watching",
      "index": "Search index",
      "queue": "Queued for indexing",
      "database": "Database size",
      "content": "Content size",
      "lastBackup": "Last backup",
      "neverBackedUp": "Never — run compendio backup",
      "backup": "Create a backup",
      "backupHelp": "Writes an archive of the content and database to the server's backups folder.",
      "backupPassphrase": "Backup passphrase",
      "backupPassphraseHint": "Required because this instance has encrypted folders. It protects the encryption key inside the archive — keep it somewhere other than this server, as you will need it to restore.",
      "backupDone": "Backup written: {{file}}",
      "reindex": "Rebuild the search index",
      "reconcile": "Re-read the content folder"
    },
    "audit": {
      "title": "Audit log",
      "action": "Action",
      "actor": "Who",
      "target": "What",
      "when": "When"
    },
    "ai": {
      "title": "AI assistant",
      "intro": "Optional. One OpenAI-compatible endpoint covers Ollama, Groq, OpenAI, Azure OpenAI, LM Studio and vLLM. With nothing configured, no AI control appears anywhere in the product.",
      "baseUrl": "Base URL",
      "baseUrlHint": "The chat-completions base, without the trailing path. Ollama: http://localhost:11434/v1",
      "model": "Model",
      "modelHint": "Use a standard instruct model, e.g. llama-3.3-70b-versatile. Reasoning models (gpt-oss, DeepSeek-R1, o-series) are slower, costlier and not recommended here.",
      "apiKey": "API key",
      "apiKeyStored": "A key is stored. Leave blank to keep it, or type a new one to replace it.",
      "apiKeyOptional": "Optional. Ollama and LM Studio do not need one.",
      "privacyOn": "Page content is sent to {{endpoint}} when somebody uses an AI action. A local model keeps it on this machine.",
      "privacyOff": "No provider is configured, so nothing is sent anywhere and no AI control is shown.",
      "test": "Test connection",
      "testOk": "{{model}} answered: {{reply}}",
      "testFailed": "No answer: {{detail}}",
      "disable": "Turn AI off",
      "limitsTitle": "Usage limits",
      "limitsIntro": "A hosted endpoint charges per request. These caps bound what this wiki can spend in a day, counted over a rolling 24 hours. 0 means no limit. A request that fails at the provider still counts, because by then it has already cost money.",
      "dailyPerUser": "Requests per person per day",
      "dailyPerUserHint": "Generous is right: an editor working through a dozen pages spends about forty.",
      "dailyPerInstance": "Requests for everyone per day",
      "dailyPerInstanceHint": "A second ceiling across the whole instance. Leave at 0 unless the endpoint is metered.",
      "usedInLastDay": "{{used}} requests in the last 24 hours.",
      "topSpenders": "Most requests",
      "scopeTitle": "Where AI may be used",
      "allowedSpaces": "Allowed spaces",
      "allowedSpacesHint": "Top-level folders the assistant may read. Leave empty for all of them.",
      "allowedSpacesPlaceholder": "IT, HR…",
      "features": "Features",
      "featuresHint": "Unchecked features disappear from the product entirely, rather than failing when used."
    },
    "git": {
      "title": "Git mirror",
      "disabled": "Disabled. Enable it with GitMirror:Enabled and a remote URL.",
      "noGit": "Enabled, but git is not on PATH. Everything else is unaffected.",
      "branch": "Pushing to branch {{branch}}.",
      "lastSuccess": "Last pushed {{when}}.",
      "neverPushed": "Enabled, nothing pushed yet.",
      "lastError": "The last push failed: {{detail}}",
      "pushNow": "Push now"
    },
    "integrations": "Integrations",
    "deleted": {
      "title": "Deleted pages",
      "intro": "Pages whose file was deleted but whose history is still kept. Restoring brings the last version back, with its whole history, where the page was.",
      "empty": "No deleted pages are waiting to be restored.",
      "path": "Where it was",
      "deletedAt": "Deleted",
      "versions": "Versions",
      "restore": "Restore",
      "restoreTo": "Restore to",
      "restoreToHint": "Leave as it is to put the page back where it was. Change it if something else is there now.",
      "restored": "The page is back: {{path}}",
      "exists": "Something is already at that path. Choose another one."
    }
  },
  "about": {
    "title": "About Compendio",
    "version": "Version {{version}}",
    "license": "Licensed under the GNU Affero General Public License, version 3 or later.",
    "source": "Source code"
  },
  "template": {
    "blank": "Blank page",
    "procedure": "Procedure",
    "runbook": "Runbook",
    "policy": "Policy",
    "meeting": "Meeting notes"
  },
  "common": {
    "discard": "Discard",
    "exportCsv": "Export CSV",
    "cancel": "Cancel",
    "save": "Save",
    "close": "Close"
  },
  "dashboard": {
    "title": "Dashboard",
    "myStalePages": "Your pages due for review",
    "viewAll": "View all",
    "nothingStale": "Nothing of yours is overdue. You own {{count}} page(s).",
    "daysOverdue": "{{count}} d overdue",
    "notifications": "Recent notifications",
    "noNotifications": "Nothing new.",
    "acknowledgmentsOwed": "You have pages to confirm you have read",
    "overdue": "Overdue"
  },
  "notifications": {
    "title": "Notifications",
    "empty": "Nothing here.",
    "markRead": "Mark read",
    "markAllRead": "Mark all read",
    "unread": "New"
  },
  "notification": {
    "PageStale": "A page you own is due for review",
    "OwnedPageEditedExternally": "A page you own was edited on disk",
    "TranslationSourceChanged": "The source of a translation you own has changed",
    "AcknowledgmentRequested": "Please confirm you have read this",
    "AcknowledgmentOverdue": "Overdue: please confirm you have read this",
    "GitMirrorFailed": "The git mirror could not push"
  },
  "stale": {
    "title": "Pages due for review",
    "count": "{{count}} page(s) past their review date",
    "empty": "Nothing is overdue.",
    "page": "Page",
    "owner": "Owner",
    "due": "Due",
    "overdue": "Overdue",
    "days": "{{count}} d",
    "unassigned": "Unassigned",
    "unassignedOnly": "No reachable owner",
    "freshnessColumn": "Freshness check"
  },
  "lifecycle": {
    "staleTitle": "This page is due for review",
    "staleBody": "Owner: {{owner}}. Check it is still correct, then confirm.",
    "confirmReviewed": "Confirm reviewed",
    "nobody": "nobody",
    "panelTitle": "Review and ownership",
    "owner": "Owner",
    "ownerHint": "Who is responsible for keeping this page correct. They get the reminders.",
    "noMatch": "No matching account",
    "unknownOwner": "{{owner}} (no matching account)",
    "interval": "Review every (days)",
    "intervalHint": "Leave empty for no review cycle. Setting this starts the clock from today.",
    "requiresAcknowledgment": "Requires acknowledgment",
    "requiresAcknowledgmentHint": "Everyone who can read this page is asked to confirm they have.",
    "currentlyDue": "Currently due {{date}}."
  },
  "acknowledgment": {
    "required": "You must confirm you have read this page",
    "body": "Read the page, then confirm. Your confirmation records this exact version.",
    "confirm": "I have read this",
    "report": "Acknowledgments",
    "progress": "{{done}} of {{total}} confirmed",
    "version": "Measured against version {{sequence}}.",
    "person": "Person",
    "status": "Status",
    "outstanding": "Not yet",
    "materialRevision": "Material revision",
    "materialRevisionHint": "Ask everyone to read it again"
  },
  "ai": {
    "menu": "AI",
    "sentTo": "Sent to {{endpoint}}",
    "improvePage": "Improve writing",
    "improveSelection": "Improve selection",
    "summarize": "Summarize",
    "freshness": "Check for outdated content",
    "proposalTitle": "Suggested change",
    "proposalNote": "From {{model}} at {{endpoint}}. Nothing is saved until you accept.",
    "proposalNoteReadOnly": "From {{model}} at {{endpoint}}. Nothing has been changed — this is for you to read.",
    "freshnessResult": "What looks out of date",
    "accept": "Use this",
    "unreviewedTitle": "Machine-translated, unreviewed",
    "unreviewedBody": "This translation was produced by a model and nobody has checked it. Saving the page from the editor clears this notice.",
    "translate": "Translate this page",
    "translateNote": "A model produces the translation and nobody has checked it. The new page is badged unreviewed until a person saves it.",
    "askTitle": "Ask the wiki",
    "askIntro": "Answered only from pages you can read, using {{endpoint}}.",
    "askPlaceholder": "What do you want to know?",
    "ask": "Ask",
    "sources": "Sources",
    "noAnswer": "Nothing in the pages you can read answers that.",
    "answeredBy": "Answered by {{model}} at {{endpoint}}.",
    "unavailableTitle": "Ask the wiki is not available",
    "unavailableBody": "No AI provider is configured on this instance, or this feature has been switched off.",
    "summarizeSelection": "Summarize selection",
    "proposalTitleSelection": "Suggested change to your selection",
    "acceptSelection": "Replace selection",
    "working": "Waiting for {{endpoint}}. A local model can take a minute or two.",
    "budgetRemaining": "{{remaining}} of {{limit}} AI requests left for you today.",
    "quotaResetsAt": "More become available around {{when}}.",
    "draft": "Draft with AI",
    "draftTitle": "Draft a page from your notes",
    "draftIntro": "Type what you know in any shape — bullet points, half sentences, the order you happen to remember it. {{endpoint}} turns it into a structured page you can edit.",
    "draftNotes": "Your notes",
    "draftNotesHint": "Rough is fine. This is what most people find easier than a blank page.",
    "draftPlaceholder": "- restart the service from the console\n- check the log at D:\\logs\n- if it fails twice, call Ana",
    "draftTemplate": "Shape it like",
    "draftTemplateHint": "Optional. Uses the same page templates as the rest of the wiki.",
    "draftUnsavedNote": "The draft lands in the editor. Nothing is saved until you save it.",
    "feature": {
      "improve": "Improve writing",
      "draft": "Draft from notes",
      "summarize": "Summarize",
      "translate": "Translate",
      "ask": "Ask the wiki",
      "freshness": "Freshness hints"
    }
  }
}
